// src/components/teams/detailCards/incomeDashboard.jsx
import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import IncomeBreakdownModal from '../components/modals/incomeBreakdownModal';
import FinanceIntegrationsModal from '../components/modals/FinanceIntegrationsModal';
import XeroIntegrationModal from '../components/modals/XeroIntegrationModal';
import { getCurrencyMeta } from '../../../utils/currency';
import { getStripeFeeConfig, getPlatformFeeMinor } from '../../../utils/fees';

/* ───────────── Helpers (match modal logic) ───────────── */

const estimateStripeFeeMinor = (amountMinor, currency) => {
  const cfg = getStripeFeeConfig({
    currency
  });
  const pct = Number(cfg.percent || 0);
  const fixed = Number(cfg.fixedMinor || 0);
  const fee = Math.round(Number(amountMinor || 0) * pct + fixed);
  return Math.max(0, fee);
};

// Fallback for legacy rows without breakdown
function platformFeePerCycle({
  renewalEvery = 1,
  renewalUnit = 'year',
  platformFeeMinor
} = {}) {
  const cyclesMap = {
    year: 1,
    month: 12,
    week: 52,
    day: 365
  };
  const cycles = cyclesMap[renewalUnit] || 1;
  return Math.round(Number(platformFeeMinor || 0) * Number(renewalEvery || 1) / cycles);
}

/** Preferred: compute club payout from row.breakdown */
function payoutFromBreakdown({
  breakdown = {},
  platformFeeHandling,
  stripeFeeHandling,
  currency
}) {
  // breakdown = { baseAmount, platformFee, stripeFee, finalAmount }
  const baseAmount = Number(breakdown.baseAmount || 0);
  const platformFee = Number(breakdown.platformFee || 0);
  const hasStoredStripeFee = Object.prototype.hasOwnProperty.call(breakdown, 'stripeFee');
  const storedStripeFee = Number(breakdown.stripeFee || 0);

  // Stripe deduction is only a club deduction when 'deduct'
  const grossForFees = baseAmount + (platformFeeHandling === 'add' ? platformFee : 0);
  const stripeDeduction = stripeFeeHandling === 'deduct' ? hasStoredStripeFee ? storedStripeFee : estimateStripeFeeMinor(grossForFees, currency) : 0;
  const platformDeduction = platformFeeHandling === 'deduct' ? platformFee : 0;
  const payout = baseAmount - platformDeduction - stripeDeduction;
  return Math.max(0, payout);
}

/** Legacy expected payout (no breakdown) */
function computeExpectedClubPayout({
  baseAmountPence,
  platformFeeHandling,
  // 'add' | 'deduct'
  stripeFeeHandling,
  // 'add' | 'deduct'
  perCyclePlatformFeePence,
  currency
}) {
  const base = Number(baseAmountPence || 0);
  const perCycle = Number(perCyclePlatformFeePence || 0);
  const grossMemberPays = platformFeeHandling === 'add' ? base + perCycle : base;
  const stripeFee = stripeFeeHandling === 'deduct' ? estimateStripeFeeMinor(grossMemberPays, currency) : 0;
  const platformDeduction = platformFeeHandling === 'deduct' ? perCycle : 0;
  return Math.max(0, base - platformDeduction - stripeFee);
}

/** Legacy paid payout (no breakdown) */
function computePaidClubPayout({
  amountPence,
  isMembership,
  isBoltOn,
  platformFeeHandling,
  stripeFeeHandling,
  perCyclePlatformFeePence,
  trustPaidAmount = false // true for bolt-on subs that store net in 'amount'
}) {
  if (trustPaidAmount) return Math.max(0, Number(amountPence || 0));
  let base = Number(amountPence || 0);

  // Membership paid rows may contain gross when platformFeeHandling='add'
  if (isMembership && platformFeeHandling === 'add') {
    base = Math.max(0, base - Number(perCyclePlatformFeePence || 0));
  }
  // Bolt-on manual: 'amount' is catalog/base → already fine.

  return computeExpectedClubPayout({
    baseAmountPence: base,
    platformFeeHandling,
    stripeFeeHandling,
    perCyclePlatformFeePence
  });
}

/* ───────────── Component ───────────── */

export default function IncomeDashboardCard({
  teamId
}) {
  const [totals, setTotals] = useState({
    expected: 0,
    paid: 0
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [xeroOpen, setXeroOpen] = useState(false);
  const [currency, setCurrency] = useState('GBP');
  useEffect(() => {
    const fetchIncomeData = async () => {
      setLoading(true);
      setErr(null);
      try {
        // Current cycle key
        const today = new Date();
        const cycle = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

        // Team fee policy (authoritative)
        const setupSnap = await getDoc(doc(db, `teams/${teamId}/stripe/setup`));
        const setup = setupSnap.exists() ? setupSnap.data() || {} : {};
        const setupCurrency = setup.currency || 'GBP';
        setCurrency(setupCurrency);
        const perYearPlatformFeeMinor = getPlatformFeeMinor({
          currency: setupCurrency,
          teamFeeCfg: setup
        });
        const teamPolicy = {
          platformFeeHandling: setup.platformFeeHandling || 'add',
          stripeFeeHandling: setup.stripeFeeHandling || 'deduct',
          renewalEvery: setup.renewalEvery || 1,
          // legacy fallback only
          renewalUnit: setup.renewalUnit || 'year' // legacy fallback only
        };
        const feePerCycleFallback = platformFeePerCycle({
          ...teamPolicy,
          platformFeeMinor: setup.platformFeeMinor ?? perYearPlatformFeeMinor
        });

        // Bolt-on overrides (stripeFeeHandling)
        const boltOnsSnap = await getDocs(collection(db, 'teams', teamId, 'boltOns'));
        const boltOnStripePolicyById = {};
        boltOnsSnap.forEach(bs => {
          const d = bs.data() || {};
          boltOnStripePolicyById[bs.id] = d.stripeFeeHandling || 'deduct';
        });

        // Load rows for this cycle
        const rowsSnap = await getDocs(collection(db, `teams/${teamId}/billingCycles/${cycle}/members`));
        let expected = 0;
        let paid = 0;
        rowsSnap.forEach(docSnap => {
          const row = docSnap.data() || {};
          const isPaid = !!row.paid;
          const method = row.method; // 'manual' | 'subscription'
          const amount = Number(row.amount || 0);
          const paymentType = row.paymentType || (docSnap.id.includes('__bo__') ? 'bolt_on' : 'membership');
          const isBoltOn = paymentType === 'bolt_on';
          const isMembership = !isBoltOn;
          const breakdown = row.breakdown || null; // { baseAmount, platformFee, stripeFee, finalAmount }

          // Per-row fee policy
          const boltOnId = row.boltOnId;
          const rowPolicy = isBoltOn ? {
            platformFeeHandling: teamPolicy.platformFeeHandling,
            stripeFeeHandling: boltOnStripePolicyById[boltOnId] || teamPolicy.stripeFeeHandling
          } : {
            platformFeeHandling: teamPolicy.platformFeeHandling,
            stripeFeeHandling: teamPolicy.stripeFeeHandling
          };
          if (isPaid) {
            // PAID → prefer exact breakdown; else legacy fallback
            const clubPaid = breakdown ? payoutFromBreakdown({
              breakdown,
              platformFeeHandling: rowPolicy.platformFeeHandling,
              stripeFeeHandling: rowPolicy.stripeFeeHandling,
              currency: row.currency || setupCurrency
            }) : computePaidClubPayout({
              amountPence: amount,
              isMembership,
              isBoltOn,
              platformFeeHandling: rowPolicy.platformFeeHandling,
              stripeFeeHandling: rowPolicy.stripeFeeHandling,
              perCyclePlatformFeePence: feePerCycleFallback,
              // For bolt-on *subscriptions*, webhook stores net in 'amount'
              trustPaidAmount: isBoltOn && method === 'subscription'
            });
            paid += clubPaid;
            expected += clubPaid; // keep expected aligned for paid rows
          } else {
            // UNPAID/PROJECTED → prefer breakdown; else legacy expectation
            const clubExpected = breakdown ? payoutFromBreakdown({
              breakdown,
              platformFeeHandling: rowPolicy.platformFeeHandling,
              stripeFeeHandling: rowPolicy.stripeFeeHandling,
              currency: row.currency || setupCurrency
            }) : computeExpectedClubPayout({
              // Legacy projections stored base/catalog in 'amount'
              baseAmountPence: amount,
              platformFeeHandling: rowPolicy.platformFeeHandling,
              stripeFeeHandling: rowPolicy.stripeFeeHandling,
              perCyclePlatformFeePence: feePerCycleFallback,
              currency: row.currency || setupCurrency
            });
            expected += clubExpected;
          }
        });
        setTotals({
          expected,
          paid
        });
      } catch (e) {
        setErr('Failed to load income summary.');
      } finally {
        setLoading(false);
      }
    };
    fetchIncomeData();
  }, [teamId]);
  const outstanding = Math.max(0, totals.expected - totals.paid);
  return <>
      <div className="team-card">
        <div className="card-header-with-icon">
          <span className="card-title">Income Overview</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="edit-btn" onClick={() => setIntegrationsOpen(true)} aria-label="Open Integrations" title="Integrations">
              <span style={{ fontSize: 12, fontWeight: 700 }}>Integrations</span>
            </button>
            <button className="edit-btn" onClick={() => setModalOpen(true)} aria-label="View Breakdown" title="View Breakdown">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2a8 8 0 105.293 14.293l5.207 5.207a1 1 0 001.414-1.414l-5.207-5.207A8 8 0 0010 2zm0 2a6 6 0 110 12 6 6 0 010-12z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="card-body">
          {loading ? <p className="text-sm text-gray-500">Loading income data…</p> : err ? <p className="text-sm text-red-600">{err}</p> : (() => {
          const {
            symbol,
            decimals
          } = getCurrencyMeta(currency);
          const fmt = minor => `${symbol}${(Number(minor || 0) / Math.pow(10, decimals)).toFixed(decimals)}`;
          return <ul className="text-sm text-gray-700">
                <li><strong>Total Expected (club payout):</strong> {fmt(totals.expected)}</li>
                <li><strong>Paid to Club:</strong> {fmt(totals.paid)}</li>
                <li><strong>Outstanding:</strong> {fmt(outstanding)}</li>
              </ul>;
        })()}
        </div>
      </div>

      <IncomeBreakdownModal isOpen={modalOpen} onClose={() => setModalOpen(false)} teamId={teamId} />
      <FinanceIntegrationsModal
        isOpen={integrationsOpen}
        onClose={() => setIntegrationsOpen(false)}
        onOpenXero={() => {
          setIntegrationsOpen(false);
          setXeroOpen(true);
        }}
        teamId={teamId}
      />
      <XeroIntegrationModal isOpen={xeroOpen} onClose={() => setXeroOpen(false)} teamId={teamId} />
    </>;
}
